import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/lib/api';
import type { User, Tenant } from '@my360/shared';

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, tenantName: string, referralCode?: string) => Promise<void>;
  /** Finishes a "Sign in with Google" round-trip (see GoogleCallback page). */
  loginWithGoogleCode: (code: string) => Promise<{ isNewUser: boolean; redirect: string | null }>;
  /** Native Android/iOS "Sign in with Google" — see GoogleSignInButton. */
  loginWithGoogleIdToken: (idToken: string, referralCode?: string | null) => Promise<{ isNewUser: boolean }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      // Subscribe ONCE to api auth-fail events. Triggered when refresh exhausts
      // (refresh token expired or invalid) → clear local state cleanly so UI
      // shows "logged out" state instead of empty-data ghost screens.
      api.onAuthFail(() => {
        const { isAuthenticated } = get();
        if (!isAuthenticated) return;
        set({ user: null, tenant: null, isAuthenticated: false, isLoading: false });
        // Soft redirect — only if currently in a protected route
        if (typeof window !== 'undefined') {
          const path = window.location.pathname;
          const isPublic = path === '/' || path.startsWith('/login') || path.startsWith('/register') || path.startsWith('/p/') || path.startsWith('/pricing');
          if (!isPublic) {
            sessionStorage.setItem('auth-fail-redirect', path);
            window.location.href = '/login';
          }
        }
      });

      return {
      user: null,
      tenant: null,
      isLoading: true,
      isAuthenticated: false,

      login: async (email, password) => {
        const response = await api.request<{
          success: boolean;
          data: {
            user: User;
            tenant: Tenant;
            tokens: { accessToken: string; refreshToken: string };
          };
        }>('/auth/login', {
          method: 'POST',
          body: { email, password },
        });

        api.setTokens(response.data.tokens.accessToken, response.data.tokens.refreshToken);

        set({
          user: response.data.user,
          tenant: response.data.tenant,
          isAuthenticated: true,
        });
      },

      register: async (email, password, name, tenantName, referralCode?: string) => {
        const body: Record<string, string> = { email, password, name, tenantName };
        if (referralCode) body['referralCode'] = referralCode;
        const response = await api.request<{
          success: boolean;
          data: {
            user: User;
            tenant: Tenant;
            tokens: { accessToken: string; refreshToken: string };
          };
        }>('/auth/register', {
          method: 'POST',
          body,
        });

        api.setTokens(response.data.tokens.accessToken, response.data.tokens.refreshToken);

        set({
          user: response.data.user,
          tenant: response.data.tenant,
          isAuthenticated: true,
        });
      },

      // Exchanges the single-use handoff code the API put in the callback URL
      // for the real access/refresh pair. Tokens never travel in the URL, so
      // this POST is what actually authenticates the browser — and it lands in
      // EXACTLY the same state as `login()` above.
      loginWithGoogleCode: async (code: string) => {
        const response = await api.request<{
          success: boolean;
          data: {
            user: User;
            tenant: Tenant;
            tokens: { accessToken: string; refreshToken: string };
            isNewUser: boolean;
            redirect: string | null;
          };
        }>('/auth/google/session', {
          method: 'POST',
          body: { code },
        });

        api.setTokens(response.data.tokens.accessToken, response.data.tokens.refreshToken);

        set({
          user: response.data.user,
          tenant: response.data.tenant,
          isAuthenticated: true,
        });

        return { isNewUser: response.data.isNewUser, redirect: response.data.redirect };
      },

      // Native counterpart of loginWithGoogleCode: the Capacitor plugin
      // already ran the native account picker and handed back a verified ID
      // token directly (no browser redirect involved), so this is a single
      // POST instead of the redirect+handoff dance — same resulting session.
      loginWithGoogleIdToken: async (idToken: string, referralCode?: string | null) => {
        const body: Record<string, string> = { idToken };
        if (referralCode) body['ref'] = referralCode;
        const response = await api.request<{
          success: boolean;
          data: {
            user: User;
            tenant: Tenant;
            tokens: { accessToken: string; refreshToken: string };
            isNewUser: boolean;
          };
        }>('/auth/google/native', {
          method: 'POST',
          body,
        });

        api.setTokens(response.data.tokens.accessToken, response.data.tokens.refreshToken);

        set({
          user: response.data.user,
          tenant: response.data.tenant,
          isAuthenticated: true,
        });

        return { isNewUser: response.data.isNewUser };
      },

      logout: async () => {
        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            await api.request('/auth/logout', {
              method: 'POST',
              body: { refreshToken },
            });
          }
        } finally {
          api.clearTokens();
          set({
            user: null,
            tenant: null,
            isAuthenticated: false,
          });
        }
      },

      checkAuth: async () => {
        set({ isLoading: true });

        if (!api.isAuthenticated()) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        try {
          const response = await api.request<{
            success: boolean;
            data: { user: User; tenant: Tenant };
          }>('/auth/me');

          set({
            user: response.data.user,
            tenant: response.data.tenant,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          api.clearTokens();
          set({
            user: null,
            tenant: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },
    };
    },
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        tenant: state.tenant,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
